/**
 * Container preference order for MediaRecorder.
 *
 * Chromium produces WebM; nothing in a browser will give us the original
 * MPEG-TS, because MediaRecorder re-encodes. MP4 is listed first because a
 * recording the user copies to a TV or phone is far more likely to play, and
 * newer Chromium supports it; WebM is the reliable fallback.
 */
const CONTAINER_PREFERENCE = [
    "video/mp4;codecs=avc1,mp4a.40.2",
    "video/mp4",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
];
/** globalThis does not structurally match the probe shape (its
 * HTMLMediaElement.prototype is concrete), so the cast goes via unknown. Tests
 * pass a stub in place of it. */
function asProbe(scope) {
    return scope;
}
/** Reads a possibly-absent method off a prototype without asserting its type. */
function hasMethod(proto, name) {
    return typeof proto?.[name] === "function";
}
/** First container this browser will actually produce, or null. */
export function pickContainer(scope = asProbe(globalThis)) {
    const isSupported = scope.MediaRecorder?.isTypeSupported;
    if (typeof isSupported !== "function")
        return null;
    for (const type of CONTAINER_PREFERENCE) {
        try {
            if (isSupported.call(scope.MediaRecorder, type))
                return type;
        }
        catch {
            // A browser that throws on a probe simply does not support that type.
        }
    }
    return null;
}
/**
 * What the BROWSER engine can do here.
 *
 * Every check is a real feature probe rather than a user-agent sniff: the
 * roadmap's acceptance criterion is that the control is hidden where it cannot
 * work, and browser sniffing gets that wrong in both directions — it hides the
 * button on capable browsers it has not heard of, and shows a broken one on
 * WebViews that report a Chrome UA while omitting the APIs.
 */
export function detectWebCapabilities(scope = asProbe(globalThis)) {
    const container = pickContainer(scope);
    const proto = scope.HTMLMediaElement?.prototype;
    const canCaptureStream = hasMethod(proto, "captureStream") || hasMethod(proto, "mozCaptureStream");
    const hasOpfs = typeof scope.navigator?.storage?.getDirectory === "function";
    // File System Access lets the user pick a real folder, including an external
    // drive on desktop. Absent in Android/Fire TV WebViews and on iOS, which is
    // exactly why external storage is a native-shell feature rather than a web one.
    const hasFilePicker = typeof scope.showSaveFilePicker === "function";
    const canRecord = Boolean(container) && canCaptureStream && (hasOpfs || hasFilePicker);
    const unavailableReason = canRecord
        ? undefined
        : !canCaptureStream
            ? "This browser cannot capture video from the player."
            : !container
                ? "This browser has no supported recording format."
                : "This browser has nowhere to save a recording.";
    return {
        canRecord,
        // MediaRecorder always re-encodes. Only a native engine copies the stream.
        streamCopy: false,
        canWriteExternalStorage: hasFilePicker,
        // A browser tab is throttled or frozen when backgrounded; there is no
        // foreground service to hold. Recording requires the app stay open.
        canRecordInBackground: false,
        // No exact alarms and no boot receiver, so nothing can fire while the app
        // is closed. Timers can still be STORED now and executed by the native
        // engine later.
        canScheduleUnattended: false,
        container: container ?? "",
        ...(unavailableReason ? { unavailableReason } : {}),
    };
}
/**
 * What a native ffmpeg engine is expected to report, documented here so the
 * shape is agreed before that engine exists. Mirrors how Sparkle does it:
 * ffmpeg over JNI copying the stream, a foreground service with a wake lock,
 * exact alarms plus a boot receiver for timers, and MANAGE_EXTERNAL_STORAGE to
 * write to the drive plugged into the box.
 */
export const NATIVE_CAPABILITIES_CONTRACT = {
    canRecord: true,
    streamCopy: true,
    canWriteExternalStorage: true,
    canRecordInBackground: true,
    canScheduleUnattended: true,
    container: "video/mp2t",
};
//# sourceMappingURL=capabilities.js.map